import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowLeft, X, AlertTriangle } from 'lucide-react';
import Layout from '@/components/Layout';
import {
  ADMIN_CPM_CAMPAIGN, ADMIN_CREATE_CPM_CAMPAIGN,
  ADMIN_UPDATE_CPM_CAMPAIGN, ADMIN_DELETE_CPM_CAMPAIGN,
  ADMIN_RECOMPUTE_CPM_CAMPAIGN_PAYOUTS,
  CAMPAIGN_CHANNEL_PICKER, CAMPAIGN_ADMIN_USER_PICKER,
  CAMPAIGN_LIBRARY_PICKER,
} from '@/queries/queries';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  AsyncMultiSelect,
  type AsyncMultiSelectOption,
} from '@/components/AsyncMultiSelect';
import {
  CurrencyMaskInput,
  DateMaskInput,
} from '@/components/MaskedInput';
import { useToast } from '@/hooks/use-toast';
import { SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import { considerCpmPlatforms } from '@/lib/campaigns/describeCampaignCpmRates';
import {
  cpmPlatformRatesFormFromCampaign,
  cpmPlatformRatesInput,
  zeroCpmPlatformRates,
  type CpmPlatformRatesFormState,
} from '@/lib/campaigns/cpmPlatformRatesForm';
import { Slider } from '@/components/ui/slider';
import WatermarkAnchorPicker from '@/components/admin/WatermarkAnchorPicker';
import WatermarkPreviewPane from '@/components/admin/WatermarkPreviewPane';
import WatermarkUploader from '@/components/admin/WatermarkUploader';
import type { WatermarkAnchor } from '@/lib/campaign-watermark-types';
import EnforcedLayoutCard from '@/components/admin/EnforcedLayoutCard';
import EnforcedSubtitleCard, { type EnforcedSubtitlePositionValue } from '@/components/admin/EnforcedSubtitleCard';
import EditingAssetsCard, { type EditingAsset } from '@/components/admin/EditingAssetsCard';
import { CAMPAIGN_CATEGORY_ICONS } from '@/components/campaigns/campaignCategoryIcons';
import type { EnforcedPostLayout } from '@/lib/layouts/applyEnforcedLayout';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { formatCents } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  CAMPAIGN_CATEGORIES,
  MAX_CAMPAIGN_CATEGORIES,
  toggleCampaignCategory,
  visibleCampaignCategories,
} from '@/lib/campaigns/campaignCategories';
import { RankingCampaignFields } from '@/components/campaigns/RankingCampaignFields';
import { downloadIdsInput } from '@/lib/campaigns/downloadIdsInput';
import { CampaignLiveImportCard } from '@/components/admin/CampaignLiveImportCard';
import { ExcludedCampaignVideos } from '@/components/admin/ExcludedCampaignVideos';
import { RankingCampaignPanel } from '@/components/campaigns/RankingCampaignPanel';
import { defaultRankingConfig, normalizeRankingConfig, rankingConfigError, rankingPrizePool, toLocalDatetime, fromLocalDatetime, rankingEndFromLocal, rankingEndToLocal, type RankingConfig } from '@/lib/campaigns/rankingCompetition';
import { MAX_MIN_CLIP_DURATION_SECONDS, minClipDurationSeconds, parseMinClipDurationInput } from '@/lib/campaigns/clipDuration';

function parsePerPlatformHashtags(text: string): { tiktok: string; instagram: string; youtube: string; facebook: string } {
  const lines = text.split(/\r?\n/);
  const result: Record<string, string[]> = {};
  let currentPlatform: string | null = null;
  let hasPlatformHeaders = false;
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(/^([A-Za-z][A-Za-z\s]{0,20})\s*[:\-—–]\s*(.*)$/);
    if (match) {
      hasPlatformHeaders = true;
      currentPlatform = match[1].trim().toUpperCase();
      result[currentPlatform] = [];
      if (match[2].trim()) result[currentPlatform].push(match[2].trim());
    } else if (currentPlatform) {
      result[currentPlatform].push(line);
    }
  }
  if (!hasPlatformHeaders) {
    const flat = text.trim();
    return { tiktok: flat, instagram: flat, youtube: flat, facebook: flat };
  }
  const get = (...keys: string[]) => {
    const key = Object.keys(result).find(k => keys.some(p => k.includes(p)));
    return key ? result[key].join(' ').trim() : '';
  };
  return {
    tiktok: get('TIKTOK'),
    instagram: get('INSTAGRAM', 'REELS'),
    youtube: get('YOUTUBE', 'SHORTS'),
    facebook: get('FACEBOOK'),
  };
}

function buildPerPlatformHashtags(tiktok: string, instagram: string, youtube: string, facebook = ''): string {
  const parts: string[] = [];
  if (tiktok) parts.push(`TIKTOK: ${tiktok}`);
  if (instagram) parts.push(`INSTAGRAM: ${instagram}`);
  if (youtube) parts.push(`YOUTUBE: ${youtube}`);
  if (facebook) parts.push(`FACEBOOK: ${facebook}`);
  return parts.join('\n');
}

type FormState = {
  campaign_type: 'cpm' | 'ranking';
  ranking_config: RankingConfig;
  name: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
  currency: 'BRL' | 'USD';
  cpm_rate_cents: number;
  budget_total_cents: number;
  min_views_threshold: number;
  min_payout_per_clipper_cents: number | null;
  max_payout_per_clipper_cents: number | null;
  max_payout_per_clip_cents: number | null;
  view_accrual_window_hours: number;
  required_hashtags_tiktok: string;
  required_hashtags_instagram: string;
  required_hashtags_youtube: string;
  required_hashtags_facebook: string;
  required_mention: string;
  min_clip_duration_seconds: number | null;
  allowed_platforms: string[];
  rules_allowed: string;
  rules_forbidden: string;
  rules_attention: string;
  rules_warning: string;
  caption_examples: string;
  requires_channel_approval: boolean;
  restrict_to_listed_videos: boolean;
  rank_min_finished_renders: number;
  invite_only: boolean;
  logo_url: string | null;
  banner_url: string | null;
  categories: string[];
  watermark_image_url: string | null;
  watermark_anchor: WatermarkAnchor;
  watermark_size_percent: number;
  watermark_opacity_percent: number;
  watermark_margin_x_percent: number;
  watermark_margin_y_percent: number;
  enforced_post_layout: EnforcedPostLayout | null;
  enforced_subtitle_position: EnforcedSubtitlePositionValue | null;
  editing_assets: EditingAsset[] | null;
  allow_cpm_override: boolean;
  cpm_override_mode: 'presets' | 'free';
  cpm_override_presets_cents: number[];
  cpm_override_min_cents: number | null;
  cpm_override_max_cents: number | null;
  pre_approval_enabled: boolean;
  pre_approval_min_score: number;
};

const CPM_PLATFORM_ICONS = { youtube: SiYoutube, instagram: SiInstagram, tiktok: SiTiktok } as const;

const empty: FormState = {
  campaign_type: 'cpm', ranking_config: defaultRankingConfig(),
  name: '', description: '', status: 'draft',
  start_date: '', end_date: '',
  currency: 'BRL', cpm_rate_cents: 0, budget_total_cents: 0,
  min_views_threshold: 0,
  min_payout_per_clipper_cents: null,
  max_payout_per_clipper_cents: null,
  max_payout_per_clip_cents: null,
  view_accrual_window_hours: 72,
  required_hashtags_tiktok: '', required_hashtags_instagram: '', required_hashtags_youtube: '',
  required_hashtags_facebook: '',
  required_mention: '', min_clip_duration_seconds: null, allowed_platforms: [],
  rules_allowed: '', rules_forbidden: '',
  rules_attention: '', rules_warning: '', caption_examples: '',
  requires_channel_approval: false,
  restrict_to_listed_videos: false,
  rank_min_finished_renders: 25,
  invite_only: false,
  logo_url: null,
  banner_url: null,
  categories: [],
  watermark_image_url: null,
  watermark_anchor: 'bottom-center',
  watermark_size_percent: 9,
  watermark_opacity_percent: 95,
  watermark_margin_x_percent: 0,
  watermark_margin_y_percent: 0,
  enforced_post_layout: null,
  enforced_subtitle_position: null,
  editing_assets: null,
  allow_cpm_override: false,
  cpm_override_mode: 'presets',
  cpm_override_presets_cents: [],
  cpm_override_min_cents: null,
  cpm_override_max_cents: null,
  pre_approval_enabled: false,
  pre_approval_min_score: 50,
};

export default function AdminCampaignEditPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const apollo = useApolloClient();

  const [form, setForm] = useState<FormState>(empty);
  const [platformRates, setPlatformRates] = useState<CpmPlatformRatesFormState>(
    () => cpmPlatformRatesFormFromCampaign(null),
  );
  const [channels, setChannels] = useState<AsyncMultiSelectOption[]>([]);
  const [downloads, setDownloads] = useState<AsyncMultiSelectOption[]>([]);
  // The video ids as loaded from the server; sent with each save so a live the
  // automatic-lives routine adds meanwhile is not dropped by this page.
  const [loadedDownloadIds, setLoadedDownloadIds] = useState<string[] | null>(null);
  const [admins, setAdmins] = useState<AsyncMultiSelectOption[]>([]);
  // "Selecionados" — a campaign restricted to a hand-picked list of clippers.
  // Kept outside `form` because the picker's option shape (label + value) is
  // not the wire shape; `onSave` maps it to `selected_user_ids`, the same way
  // `admins` maps to `campaign_admin_user_ids`. Mutually exclusive with
  // `invite_only`; the backend rejects both-true with
  // `selected_and_invite_only_exclusive`.
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [selectedClippers, setSelectedClippers] = useState<AsyncMultiSelectOption[]>([]);
  const [channelPickerOpen, setChannelPickerOpen] = useState(false);
  const [channelPickerQuery, setChannelPickerQuery] = useState('');
  const [channelPickerResults, setChannelPickerResults] = useState<AsyncMultiSelectOption[]>([]);
  const [channelPickerLoading, setChannelPickerLoading] = useState(false);
  const [newPresetCents, setNewPresetCents] = useState<number | null>(null);
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [rankingFinalized, setRankingFinalized] = useState(false);

  const { data, refetch } = useQuery(ADMIN_CPM_CAMPAIGN, {
    variables: { id: id || '' },
    skip: isNew,
  });
  const [createMut] = useMutation(ADMIN_CREATE_CPM_CAMPAIGN);
  const [updateMut] = useMutation(ADMIN_UPDATE_CPM_CAMPAIGN);
  const [deleteMut] = useMutation(ADMIN_DELETE_CPM_CAMPAIGN);
  const [recomputeMut, { loading: recomputing }] = useMutation(
    ADMIN_RECOMPUTE_CPM_CAMPAIGN_PAYOUTS,
  );

  useEffect(() => setRankingFinalized(false), [id]);

  useEffect(() => {
    if (isNew || !data?.adminCpmCampaign) return;
     
    const c: any = data.adminCpmCampaign;
    setForm({
      campaign_type: c.campaign_type === 'ranking' ? 'ranking' : 'cpm',
      ranking_config: normalizeRankingConfig(c.ranking_config),
      name: c.name, description: c.description || '', status: c.status,
      start_date: c.start_date || '', end_date: c.end_date || '',
      currency: (c.currency === 'USD' ? 'USD' : 'BRL'),
      cpm_rate_cents: c.cpm_rate_cents, budget_total_cents: c.budget_total_cents,
      min_views_threshold: c.min_views_threshold || 0,
      min_payout_per_clipper_cents: c.min_payout_per_clipper_cents,
      max_payout_per_clipper_cents: c.max_payout_per_clipper_cents,
      max_payout_per_clip_cents: c.max_payout_per_clip_cents,
      view_accrual_window_hours: c.view_accrual_window_hours ?? 72,
      allow_cpm_override: !!c.allow_cpm_override,
      cpm_override_mode: c.cpm_override_mode === 'free' ? 'free' : 'presets',
      cpm_override_presets_cents: Array.isArray(c.cpm_override_presets_cents)
        ? c.cpm_override_presets_cents.filter((v: unknown) => Number.isInteger(v))
        : [],
      cpm_override_min_cents: c.cpm_override_min_cents ?? null,
      cpm_override_max_cents: c.cpm_override_max_cents ?? null,
      ...(() => {
        const p = parsePerPlatformHashtags(c.required_hashtags || '');
        return { required_hashtags_tiktok: p.tiktok, required_hashtags_instagram: p.instagram, required_hashtags_youtube: p.youtube, required_hashtags_facebook: p.facebook };
      })(),
      required_mention: c.required_mention || '',
      min_clip_duration_seconds: minClipDurationSeconds(c),
      allowed_platforms: Array.isArray(c.allowed_platforms) ? c.allowed_platforms : [],
      pre_approval_enabled: !!c.pre_approval_enabled,
      pre_approval_min_score: Number.isInteger(c.pre_approval_min_score) ? c.pre_approval_min_score : 50,
      rules_allowed: c.rules_allowed || '', rules_forbidden: c.rules_forbidden || '',
      rules_attention: c.rules_attention || '', rules_warning: c.rules_warning || '',
      caption_examples: c.caption_examples || '',
      requires_channel_approval: !!c.requires_channel_approval,
      restrict_to_listed_videos: !!c.restrict_to_listed_videos,
      rank_min_finished_renders: c.rank_min_finished_renders ?? 25,
      invite_only: !!(c as any).invite_only,
      logo_url: c.logo_url ?? null,
      banner_url: c.banner_url ?? null,
      categories: visibleCampaignCategories(c.categories),
      watermark_image_url: c.watermark_image_url ?? null,
      watermark_anchor: (c.watermark_anchor as WatermarkAnchor) ?? 'bottom-center',
      watermark_size_percent: c.watermark_size_percent ?? 9,
      watermark_opacity_percent: c.watermark_opacity_percent ?? 95,
      watermark_margin_x_percent: c.watermark_margin_x_percent ?? 0,
      watermark_margin_y_percent: c.watermark_margin_y_percent ?? 0,
      enforced_post_layout: c.enforced_post_layout ?? null,
      enforced_subtitle_position: c.enforced_subtitle_position ?? null,
      editing_assets: c.editing_assets ?? null,
    });
    setPlatformRates(cpmPlatformRatesFormFromCampaign(c.cpm_platform_rates));
    setChannels(
       
      (c.youtube_channels || []).map((ch: any) => ({
        id: ch.id,
        label: ch.name,
        sublabel: ch.channel_id,
        thumbnail: ch.thumbnail,
      })),
    );
    setLoadedDownloadIds((c.youtube_downloads || []).map((d: any) => d.id));
    setDownloads(

      (c.youtube_downloads || []).map((d: any) => ({
        id: d.id,
        label: d.name || d.video_id,
        sublabel: (d.youtube_channels || [])

          .map((ch: any) => ch.name).join(' · ') || null,
        thumbnail: d.thumbnail_url,
      })),
    );
    setAdmins(
      (c.campaign_admins || []).map((u: any) => ({
        id: u.id,
        label: u.name || u.email || u.id,
        sublabel: u.email,
      })),
    );
    setSelectedOnly(!!c.selected_only);
    setSelectedClippers(
      (c.selected_users || []).map((u: any) => ({
        id: u.id,
        label: u.name || u.email || u.id,
        sublabel: u.email,
      })),
    );
  }, [data, isNew]);

  const searchChannels = useMemo(
    () => async (q: string): Promise<AsyncMultiSelectOption[]> => {
      const res = await apollo.query({
        query: CAMPAIGN_CHANNEL_PICKER,
        variables: { search: q || undefined },
        fetchPolicy: 'network-only',
      });
       
      return (res.data?.communityChannels || []).slice(0, 50).map((ch: any) => ({
        id: ch.id,
        label: ch.name,
        sublabel: ch.channel_id,
        thumbnail: ch.thumbnail,
      }));
    },
    [apollo],
  );

  const searchDownloads = useMemo(
    () => async (q: string): Promise<AsyncMultiSelectOption[]> => {
      const res = await apollo.query({
        query: CAMPAIGN_LIBRARY_PICKER,
        variables: { search: q || undefined, limit: 50 },
        fetchPolicy: 'network-only',
      });
      return (res.data?.communityLibraries || [])

        .filter((l: any) => !!l.youtube_download_id)

        .map((l: any) => ({
          id: l.youtube_download_id,
          label: l.name,
          sublabel: (l.youtube_channels || [])

            .map((ch: any) => ch.name).join(' · ') || null,
          thumbnail: l.thumbnail_url,
        }));
    },
    [apollo],
  );

  // When the "add all from channel" popover is open, fetch matching channels.
  useEffect(() => {
    if (!channelPickerOpen) return undefined;
    let cancelled = false;
    setChannelPickerLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apollo.query({
          query: CAMPAIGN_CHANNEL_PICKER,
          variables: { search: channelPickerQuery || undefined },
          fetchPolicy: 'network-only',
        });
        if (!cancelled) {
          setChannelPickerResults(
             
            (res.data?.communityChannels || []).slice(0, 50).map((ch: any) => ({
              id: ch.id,
              label: ch.name,
              sublabel: ch.channel_id,
              thumbnail: ch.thumbnail,
            })),
          );
        }
      } catch {
        if (!cancelled) setChannelPickerResults([]);
      } finally {
        if (!cancelled) setChannelPickerLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [channelPickerOpen, channelPickerQuery, apollo]);

  const addAllFromChannel = useCallback(
    async (channelDocumentId: string) => {
      setChannelPickerOpen(false);
      const res = await apollo.query({
        query: CAMPAIGN_LIBRARY_PICKER,
        variables: { channelId: channelDocumentId, limit: 500 },
        fetchPolicy: 'network-only',
      });
       
      const fetched = (res.data?.communityLibraries || [])

        .filter((l: any) => !!l.youtube_download_id)

        .map((l: any) => ({
          id: l.youtube_download_id,
          label: l.name,
          sublabel: (l.youtube_channels || [])

            .map((ch: any) => ch.name).join(' · ') || null,
          thumbnail: l.thumbnail_url,
        }));
      setDownloads((prev) => {
        const existing = new Set(prev.map((p) => p.id));
        const additions = fetched.filter(
          (opt: AsyncMultiSelectOption) => !existing.has(opt.id),
        );
        return [...prev, ...additions];
      });
    },
    [apollo],
  );

  const searchAdmins = useMemo(
    () => async (q: string): Promise<AsyncMultiSelectOption[]> => {
      const res = await apollo.query({
        query: CAMPAIGN_ADMIN_USER_PICKER,
        variables: { search: q || undefined, pageSize: 20 },
        fetchPolicy: 'network-only',
      });
       
      return (res.data?.adminUsers?.data || []).map((u: any) => ({
        id: u.id,
        label: u.name || u.email || u.id,
        sublabel: u.email,
      }));
    },
    [apollo],
  );

  // Shared preview inputs: every enforcement card previews the full composition.
  const previewWatermark = useMemo(() => ({
    imageUrl: form.watermark_image_url,
    anchor: form.watermark_anchor,
    sizePercent: form.watermark_size_percent,
    opacityPercent: form.watermark_opacity_percent,
    marginXPercent: form.watermark_margin_x_percent,
    marginYPercent: form.watermark_margin_y_percent,
  }), [form.watermark_image_url, form.watermark_anchor, form.watermark_size_percent, form.watermark_opacity_percent, form.watermark_margin_x_percent, form.watermark_margin_y_percent]);

  const previewPostLayout = useMemo(() => (
    form.enforced_post_layout
      ? ({ kind: form.enforced_post_layout.kind, ...form.enforced_post_layout.config } as unknown as PostLayoutConfig)
      : null
  ), [form.enforced_post_layout]);

  const onSave = async () => {
    if (form.campaign_type === 'ranking') {
      const validationError = rankingConfigError(form.ranking_config, form.end_date, form.currency);
      if (validationError || !form.start_date || !form.end_date || Date.parse(form.start_date) >= Date.parse(form.end_date)) {
        toast({ variant: 'destructive', title: t('admin.campaigns.edit.toast.saveErrorTitle'), description: t(`ranking.errors.${validationError || 'invalidPeriod'}`) });
        return;
      }
    }
    if (form.campaign_type !== 'ranking'
      && zeroCpmPlatformRates(platformRates, form.allowed_platforms).length > 0) {
      toast({
        variant: 'destructive',
        title: t('admin.campaigns.edit.toast.saveErrorTitle'),
        description: t('admin.campaigns.edit.cpmPerPlatformErrors.cpm_platform_rate_invalid'),
      });
      return;
    }
    const { required_hashtags_tiktok, required_hashtags_instagram, required_hashtags_youtube, required_hashtags_facebook, ...formRest } = form;
    const input = {
      ...formRest,
      ranking_config: form.campaign_type === 'ranking' ? form.ranking_config : null,
      ...(form.campaign_type === 'ranking' ? {
        cpm_rate_cents: 0, budget_total_cents: rankingPrizePool(form.ranking_config),
        allow_cpm_override: false, pre_approval_enabled: false,
      } : {}),
      cpm_platform_rates: form.campaign_type === 'ranking'
        ? []
        : cpmPlatformRatesInput(platformRates, form.allowed_platforms),
      required_hashtags: buildPerPlatformHashtags(required_hashtags_tiktok, required_hashtags_instagram, required_hashtags_youtube, form.campaign_type === 'ranking' ? required_hashtags_facebook : ''),
      youtube_channel_ids: channels.map((c) => c.id),
      ...downloadIdsInput({ current: downloads.map((d) => d.id), loaded: isNew ? null : loadedDownloadIds }),
      campaign_admin_user_ids: admins.map((a) => a.id),
      selected_only: selectedOnly,
      // Always sent, even when empty: an omitted `selected_user_ids` leaves the
      // existing relation untouched on the backend, so clearing the list has to
      // go over the wire as `[]`.
      selected_user_ids: selectedClippers.map((u) => u.id),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      description: form.description || null,
    };
    try {
      if (isNew) {
        const res = await createMut({ variables: { input } });
        toast({
          title: t('admin.campaigns.edit.toast.createSuccessTitle'),
          description: t('admin.campaigns.edit.toast.createSuccessDescription'),
        });
         
        navigate(`/admin/campaigns/${(res.data as any).adminCreateCpmCampaign.id}`);
      } else {
        await updateMut({ variables: { id, input } });
        toast({
          title: t('admin.campaigns.edit.toast.saveSuccessTitle'),
          description: t('admin.campaigns.edit.toast.saveSuccessDescription'),
        });
      }
     
    } catch (err: any) {
      const code = String(err?.message || '').replace(/^.*?:\s*/, '').trim();
      const categoryError = code === 'invalid_category' || code === 'too_many_categories'
        ? t(`admin.campaigns.edit.categories.errors.${code}`)
        : null;
      const platformRateError = [
        'cpm_platform_rate_invalid',
        'cpm_platform_rate_duplicate',
        'cpm_platform_rate_platform_not_allowed',
      ].includes(code) ? t(`admin.campaigns.edit.cpmPerPlatformErrors.${code}`) : null;
      const rankingError = code.startsWith('ranking_') ? t(
        ['ranking_type_locked', 'ranking_period_locked', 'ranking_finalized', 'ranking_immutable_history'].includes(code)
          ? 'ranking.errors.settingsLocked' : 'ranking.errors.saveFailed',
      ) : null;
      toast({
        variant: 'destructive',
        title: t('admin.campaigns.edit.toast.saveErrorTitle'),
        description: categoryError || platformRateError || rankingError || err?.message || String(err),
      });
    }
  };

  const onDelete = async () => {
     
    if (!confirm(t('admin.campaigns.edit.deleteConfirm'))) return;
    try {
      await deleteMut({ variables: { id, force: false } });
      toast({ title: t('admin.campaigns.edit.toast.deleteSuccessTitle') });
      navigate('/admin/campaigns');
     
    } catch (err: any) {
       
      if (err.message.includes('force=true') && confirm(t('admin.campaigns.edit.forceDeleteConfirm'))) {
        try {
          await deleteMut({ variables: { id, force: true } });
          toast({ title: t('admin.campaigns.edit.toast.deleteSuccessTitle') });
          navigate('/admin/campaigns');
         
        } catch (forceErr: any) {
          toast({
            variant: 'destructive',
            title: t('admin.campaigns.edit.toast.deleteErrorTitle'),
            description: forceErr?.message || String(forceErr),
          });
        }
        return;
      }
      toast({
        variant: 'destructive',
        title: t('admin.campaigns.edit.toast.deleteErrorTitle'),
        description: err?.message || String(err),
      });
    }
  };

  // Re-price every existing clip against the campaign's CURRENT settings. Used
  // after raising `min_views_threshold` (or another economics field) so clips
  // that no longer clear the bar are pulled down and their spend refunded to
  // the budget. Can DECREASE payouts — hence the explicit confirm dialog.
  const onRecompute = async () => {
    if (isNew || !id) return;
    try {
      const res = await recomputeMut({ variables: { campaignId: id } });
      const result = res?.data?.adminRecomputeCpmCampaignPayouts;
      const changed = result?.changed_count ?? 0;
      const delta = result?.total_delta_cents ?? 0;
      setRecomputeOpen(false);
      toast({
        title: t('admin.campaigns.edit.recompute.successTitle'),
        description: t('admin.campaigns.edit.recompute.successDescription', {
          changed,
          delta: formatCents(delta, form.currency),
        }),
      });
      await refetch();

    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: t('admin.campaigns.edit.recompute.errorTitle'),
        description: err?.message || String(err),
      });
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/campaigns')}
                aria-label={t('admin.campaigns.backToList')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Sparkles className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">
                  {isNew
                    ? t('admin.campaigns.edit.newTitle')
                    : t('admin.campaigns.edit.editTitle')}
                </h1>
                <p className="text-muted-foreground">
                  {isNew
                    ? t('admin.campaigns.edit.newSubtitle')
                    : t('admin.campaigns.edit.editSubtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
              {!isNew && id && (
                <div className="flex items-center gap-1 border border-border rounded-md p-1">
                  <Button variant="default" size="sm" disabled>
                    {t('admin.campaigns.edit.tabs.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/campaigns/${id}/submissions`)}
                  >
                    {t('admin.campaigns.edit.tabs.submissions')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/campaigns/${id}/participants`)}
                  >
                    {t('admin.campaigns.edit.tabs.participants')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {rankingFinalized && <p role="status" className="text-sm text-muted-foreground">{t('ranking.finalizedLocked')}</p>}
          <fieldset disabled={rankingFinalized} className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.campaigns.edit.detailsCard')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="campaign-type">{t('ranking.campaignType')}</Label>
                <Select value={form.campaign_type} onValueChange={value => setForm({ ...form, campaign_type: value as FormState['campaign_type'], allowed_platforms: value === 'ranking' ? form.allowed_platforms : form.allowed_platforms.filter(platform => platform !== 'facebook') })}>
                  <SelectTrigger id="campaign-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cpm">{t('ranking.types.cpm')}</SelectItem><SelectItem value="ranking">{t('ranking.types.ranking')}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.name')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.logo')}</Label>
                <WatermarkUploader
                  value={form.logo_url}
                  onChange={(url) => setForm({ ...form, logo_url: url })}
                  i18nPrefix="admin.campaigns.edit.logo"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.banner')}</Label>
                <WatermarkUploader
                  value={form.banner_url}
                  onChange={(url) => setForm({ ...form, banner_url: url })}
                  i18nPrefix="admin.campaigns.edit.banner"
                  accept={['image/png', 'image/jpeg', 'image/webp']}
                  previewAspect="video"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.description')}</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.categories')}</Label>
                <div className="flex flex-wrap gap-2" data-testid="campaign-categories-picker">
                  {CAMPAIGN_CATEGORIES.map((key) => {
                    const Icon = CAMPAIGN_CATEGORY_ICONS[key];
                    const picked = form.categories.includes(key);
                    const full = form.categories.length >= MAX_CAMPAIGN_CATEGORIES;
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={picked}
                        disabled={!picked && full}
                        onClick={() => setForm({ ...form, categories: toggleCampaignCategory(form.categories, key) })}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                          picked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-muted text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {t(`campaignCategories.${key}`)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">{t('admin.campaigns.edit.categories.hint')}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.status')}</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t('admin.campaigns.status.draft')}</SelectItem>
                      <SelectItem value="active">{t('admin.campaigns.status.active')}</SelectItem>
                      <SelectItem value="paused">{t('admin.campaigns.status.paused')}</SelectItem>
                      <SelectItem value="ended">{t('admin.campaigns.status.ended')}</SelectItem>
                      <SelectItem value="scheduled">{t('admin.campaigns.status.scheduled')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t(`admin.campaigns.statusDescriptions.${form.status}`)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.currency')}</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setForm({ ...form, currency: v as 'BRL' | 'USD' })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BRL">BRL</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.startDate')}</Label>
                  {form.campaign_type === 'ranking' ? <Input type="datetime-local" aria-label={t('admin.campaigns.edit.fields.startDate')} value={toLocalDatetime(form.start_date)} onChange={event => setForm({ ...form, start_date: fromLocalDatetime(event.target.value) || '' })} /> : <DateMaskInput
                    value={form.start_date}
                    onChange={(iso) => setForm({ ...form, start_date: iso })}
                  />}
                  {form.status === 'scheduled' && (
                    <p className="text-xs text-muted-foreground">
                      {t('admin.campaigns.edit.fields.startDateScheduledHint')}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.endDate')}</Label>
                  {form.campaign_type === 'ranking' ? <><Input type="datetime-local" aria-label={t('admin.campaigns.edit.fields.endDate')} value={rankingEndToLocal(form.end_date)} onChange={event => setForm({ ...form, end_date: rankingEndFromLocal(event.target.value) || '' })} /><p className="text-xs text-muted-foreground">{t('ranking.endExclusiveHelp')} {t('ranking.localTimeHelp')}</p></> : <DateMaskInput
                    value={form.end_date}
                    onChange={(iso) => setForm({ ...form, end_date: iso })}
                  />}
                </div>
              </div>
            </CardContent>
          </Card>

          {form.campaign_type === 'ranking' ? <Card><CardContent className="pt-6"><RankingCampaignFields config={form.ranking_config} currency={form.currency} campaignId={id} campaignStatus={data?.adminCpmCampaign?.status} onChange={ranking_config => setForm({ ...form, ranking_config })} /></CardContent></Card> : <Card>
            <CardHeader>
              <CardTitle>{t('admin.campaigns.edit.economicsCard')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.cpmRate')}</Label>
                  <CurrencyMaskInput
                    valueCents={form.cpm_rate_cents}
                    onChangeCents={(c) =>
                      setForm({ ...form, cpm_rate_cents: c ?? 0 })}
                    currency={form.currency}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.budgetTotal')}</Label>
                  <CurrencyMaskInput
                    valueCents={form.budget_total_cents}
                    onChangeCents={(c) =>
                      setForm({ ...form, budget_total_cents: c ?? 0 })}
                    currency={form.currency}
                  />
                </div>
              </div>

              <div data-testid="cpm-per-platform-section" className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="cpm-per-platform"
                    checked={platformRates.enabled}
                    onCheckedChange={(v) => setPlatformRates({ ...platformRates, enabled: v === true })}
                  />
                  <label htmlFor="cpm-per-platform" className="text-sm font-medium cursor-pointer">
                    {t('admin.campaigns.edit.fields.cpmPerPlatform')}
                  </label>
                </div>
                {platformRates.enabled && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {considerCpmPlatforms(form.allowed_platforms).map((platform) => {
                        const Icon = CPM_PLATFORM_ICONS[platform];
                        return (
                          <div key={platform} className="space-y-1">
                            <Label htmlFor={`cpm-rate-${platform}`} className="inline-flex items-center gap-1.5">
                              <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                              {t(`campaign.channels.platformLabel.${platform}`)}
                            </Label>
                            <CurrencyMaskInput
                              id={`cpm-rate-${platform}`}
                              valueCents={platformRates.rates[platform] ?? null}
                              onChangeCents={(cents) => setPlatformRates({
                                ...platformRates,
                                rates: { ...platformRates.rates, [platform]: cents },
                              })}
                              currency={form.currency}
                              placeholder={formatCents(form.cpm_rate_cents, form.currency)}
                              allowEmpty
                            />
                            {platformRates.rates[platform] === 0 && (
                              <p
                                data-testid={`cpm-rate-${platform}-zero`}
                                className="text-xs text-destructive"
                              >
                                {t('admin.campaigns.edit.cpmPerPlatformErrors.cpm_platform_rate_invalid')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('admin.campaigns.edit.fields.cpmPerPlatformHint')}
                    </p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.minViews')}</Label>
                  <Input
                    type="number"
                    value={form.min_views_threshold}
                    onChange={(e) =>
                      setForm({ ...form, min_views_threshold: Number(e.target.value) })}
                  />
                  {!isNew && (
                    <p className="text-xs text-muted-foreground">
                      {t('admin.campaigns.edit.fields.minViewsHint')}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.maxPayoutPerClip')}</Label>
                  <CurrencyMaskInput
                    valueCents={form.max_payout_per_clip_cents}
                    onChangeCents={(c) =>
                      setForm({ ...form, max_payout_per_clip_cents: c })}
                    currency={form.currency}
                    allowEmpty
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.viewAccrualWindow')}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.view_accrual_window_hours}
                    onChange={(e) =>
                      setForm({ ...form, view_accrual_window_hours: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.edit.fields.viewAccrualWindowHint')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.minPayout')}</Label>
                  <CurrencyMaskInput
                    valueCents={form.min_payout_per_clipper_cents}
                    onChangeCents={(c) =>
                      setForm({ ...form, min_payout_per_clipper_cents: c })}
                    currency={form.currency}
                    allowEmpty
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.fields.maxPayout')}</Label>
                  <CurrencyMaskInput
                    valueCents={form.max_payout_per_clipper_cents}
                    onChangeCents={(c) =>
                      setForm({ ...form, max_payout_per_clipper_cents: c })}
                    currency={form.currency}
                    allowEmpty
                  />
                </div>
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="allow-cpm-override"
                    checked={form.allow_cpm_override}
                    onCheckedChange={(v) =>
                      setForm({ ...form, allow_cpm_override: v === true })}
                  />
                  <div className="space-y-1">
                    <label htmlFor="allow-cpm-override" className="font-medium cursor-pointer">
                      {t('admin.campaigns.edit.cpmOverride.allow')}
                    </label>
                    <p className="text-sm text-muted-foreground">
                      {t('admin.campaigns.edit.cpmOverride.allowHelp')}
                    </p>
                  </div>
                </div>
                {form.allow_cpm_override && (
                  <>
                    <div className="space-y-2 max-w-xs">
                      <Label>{t('admin.campaigns.edit.cpmOverride.mode')}</Label>
                      <Select
                        value={form.cpm_override_mode}
                        onValueChange={(v) =>
                          setForm({ ...form, cpm_override_mode: v as 'presets' | 'free' })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="presets">{t('admin.campaigns.edit.cpmOverride.modePresets')}</SelectItem>
                          <SelectItem value="free">{t('admin.campaigns.edit.cpmOverride.modeFree')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.cpm_override_mode === 'presets' ? (
                      <div className="space-y-2">
                        <Label>{t('admin.campaigns.edit.cpmOverride.presets')}</Label>
                        {form.cpm_override_presets_cents.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2">
                            {form.cpm_override_presets_cents.map((cents, i) => (
                              <span
                                key={`${cents}-${i}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-muted text-sm"
                              >
                                {formatCents(cents, form.currency)}
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-red-500"
                                  aria-label={t('admin.campaigns.edit.cpmOverride.removePreset')}
                                  onClick={() => setForm({
                                    ...form,
                                    cpm_override_presets_cents:
                                      form.cpm_override_presets_cents.filter((_, j) => j !== i),
                                  })}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 max-w-xs">
                          <CurrencyMaskInput
                            valueCents={newPresetCents}
                            onChangeCents={(c) => setNewPresetCents(c)}
                            currency={form.currency}
                            allowEmpty
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={newPresetCents == null}
                            onClick={() => {
                              if (newPresetCents == null) return;
                              if (!form.cpm_override_presets_cents.includes(newPresetCents)) {
                                setForm({
                                  ...form,
                                  cpm_override_presets_cents:
                                    [...form.cpm_override_presets_cents, newPresetCents]
                                      .sort((a, b) => a - b),
                                });
                              }
                              setNewPresetCents(null);
                            }}
                          >
                            {t('admin.campaigns.edit.cpmOverride.addPreset')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>{t('admin.campaigns.edit.cpmOverride.min')}</Label>
                          <CurrencyMaskInput
                            valueCents={form.cpm_override_min_cents}
                            onChangeCents={(c) =>
                              setForm({ ...form, cpm_override_min_cents: c })}
                            currency={form.currency}
                            allowEmpty
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('admin.campaigns.edit.cpmOverride.max')}</Label>
                          <CurrencyMaskInput
                            valueCents={form.cpm_override_max_cents}
                            onChangeCents={(c) =>
                              setForm({ ...form, cpm_override_max_cents: c })}
                            currency={form.currency}
                            allowEmpty
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>}

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.campaigns.edit.rulesCard')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.requiredHashtags')}</Label>
                {form.campaign_type === 'ranking' && <div className="space-y-1"><Label htmlFor="facebook-markup">{t('ranking.platforms.facebook')}</Label><Input id="facebook-markup" value={form.required_hashtags_facebook} onChange={event => setForm({ ...form, required_hashtags_facebook: event.target.value })} placeholder="#hashtag @perfil" /></div>}
                <div className="space-y-2">
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">TikTok</span>
                    <Input
                      value={form.required_hashtags_tiktok}
                      onChange={(e) => setForm({ ...form, required_hashtags_tiktok: e.target.value })}
                      placeholder="#hashtag @perfil"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-pink-400 uppercase tracking-wider">Instagram</span>
                    <Input
                      value={form.required_hashtags_instagram}
                      onChange={(e) => setForm({ ...form, required_hashtags_instagram: e.target.value })}
                      placeholder="#hashtag @perfil"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-red-400 uppercase tracking-wider">YouTube</span>
                    <Input
                      value={form.required_hashtags_youtube}
                      onChange={(e) => setForm({ ...form, required_hashtags_youtube: e.target.value })}
                      placeholder="#hashtag @perfil"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="required-mention">
                  {t('admin.campaigns.edit.fields.requiredMention')}
                </Label>
                <Input
                  id="required-mention"
                  value={form.required_mention}
                  onChange={(e) => setForm({ ...form, required_mention: e.target.value })}
                  placeholder="@perfil"
                />
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.edit.fields.requiredMentionHint')}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="min-clip-duration">
                  {t('admin.campaigns.edit.fields.minClipDuration')}
                </Label>
                <Input
                  id="min-clip-duration"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_MIN_CLIP_DURATION_SECONDS}
                  step={1}
                  className="max-w-[12rem]"
                  value={form.min_clip_duration_seconds ?? ''}
                  onChange={(e) => setForm({ ...form, min_clip_duration_seconds: parseMinClipDurationInput(e.target.value) })}
                  placeholder={t('admin.campaigns.edit.fields.minClipDurationPlaceholder')}
                />
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.edit.fields.minClipDurationHint')}
                </p>
              </div>
              {(
                [
                  ['rules_allowed', 'rulesAllowed'],
                  ['rules_forbidden', 'rulesForbidden'],
                  ['rules_attention', 'rulesAttention'],
                  ['rules_warning', 'rulesWarning'],
                ] as const
              ).map(([formKey, i18nKey]) => (
                <div key={formKey} className="space-y-2">
                  <Label>{t(`admin.campaigns.edit.fields.${i18nKey}`)}</Label>
                  <Textarea
                    value={form[formKey]}
                    onChange={(e) => setForm({ ...form, [formKey]: e.target.value })}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.captionExamples')}</Label>
                <Textarea
                  value={form.caption_examples}
                  onChange={(e) => setForm({ ...form, caption_examples: e.target.value })}
                  rows={10}
                />
                <p className="text-xs text-muted-foreground">
                  {t('admin.campaigns.edit.fields.captionExamplesHelpIntro')}
                </p>
                <pre className="text-xs bg-muted rounded p-3 text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {t('admin.campaigns.edit.fields.captionExamplesHelpExample')}
                </pre>
                <p className="text-xs text-muted-foreground">
                  {t('admin.campaigns.edit.fields.captionExamplesHelpDica')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="requires-channel-approval"
                  checked={form.requires_channel_approval}
                  onCheckedChange={(v) =>
                    setForm({ ...form, requires_channel_approval: v === true })}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="requires-channel-approval"
                    className="font-medium cursor-pointer"
                  >
                    {t('admin.campaigns.edit.requiresChannelApproval')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.campaigns.edit.requiresChannelApprovalHelp')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="restrict-to-listed-videos"
                  checked={form.restrict_to_listed_videos}
                  onCheckedChange={(v) =>
                    setForm({ ...form, restrict_to_listed_videos: v === true })}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="restrict-to-listed-videos"
                    className="font-medium cursor-pointer"
                  >
                    {t('admin.campaigns.edit.restrictToListedVideos')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.campaigns.edit.restrictToListedVideosHelp')}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rank-min-finished-renders">
                  {t('admin.campaigns.edit.fields.rankMinFinishedRenders')}
                </Label>
                <Input
                  id="rank-min-finished-renders"
                  type="number"
                  min={1}
                  step={1}
                  value={form.rank_min_finished_renders}
                  onChange={(event) => setForm({
                    ...form,
                    rank_min_finished_renders: Math.max(
                      1,
                      Math.floor(Number(event.target.value) || 1),
                    ),
                  })}
                />
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.edit.fields.rankMinFinishedRendersHint')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="invite-only"
                  checked={form.invite_only}
                  disabled={selectedOnly}
                  onCheckedChange={(v) =>
                    setForm({ ...form, invite_only: v === true })}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="invite-only"
                    className="font-medium cursor-pointer"
                  >
                    {t('admin.campaigns.edit.inviteOnly')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.campaigns.edit.inviteOnlyHelp')}
                  </p>
                </div>
              </div>

              {/* "Selecionados" — mutuamente exclusivo com invite-only:
                  desliga o outro ao ativar. O backend rejeita os dois ligados
                  ao mesmo tempo (selected_and_invite_only_exclusive). */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="selected-only"
                  checked={selectedOnly}
                  onCheckedChange={(v) => {
                    const next = v === true;
                    setSelectedOnly(next);
                    if (next) setForm({ ...form, invite_only: false });
                  }}
                />
                <div className="space-y-1 flex-1">
                  <label
                    htmlFor="selected-only"
                    className="font-medium cursor-pointer"
                  >
                    {t('admin.campaigns.edit.selectedOnly.label')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.campaigns.edit.selectedOnly.help')}
                  </p>
                  {selectedOnly && (
                    <div className="space-y-3 pt-2">
                      {/* An empty list saves fine — the backend accepts it so a
                          campaign can be created and curated afterwards — but
                          until someone is added, nobody can see or join it. */}
                      {selectedClippers.length === 0 && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{t('admin.campaigns.edit.selectedOnly.emptyListWarning')}</span>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>{t('admin.campaigns.edit.selectedOnly.pickerLabel')}</Label>
                        <AsyncMultiSelect
                          value={selectedClippers}
                          onChange={setSelectedClippers}
                          search={searchAdmins}
                          placeholder={t('admin.campaigns.edit.selectedOnly.pickerPlaceholder')}
                          searchPlaceholder={t('admin.campaigns.edit.fields.adminsSearchPlaceholder')}
                          emptyText={t('admin.campaigns.edit.fields.emptyResults')}
                          loadingText={t('admin.campaigns.edit.fields.loading')}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.campaigns.edit.watermark.cardTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <WatermarkUploader
                value={form.watermark_image_url}
                onChange={(url) => setForm({ ...form, watermark_image_url: url })}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('admin.campaigns.edit.watermark.anchorLabel')}</Label>
                    <WatermarkAnchorPicker
                      value={form.watermark_anchor}
                      onChange={(anchor) => setForm({ ...form, watermark_anchor: anchor })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {t('admin.campaigns.edit.watermark.sizeLabel', { value: form.watermark_size_percent })}
                    </Label>
                    <Slider
                      min={5}
                      max={25}
                      step={1}
                      value={[form.watermark_size_percent]}
                      onValueChange={([v]) => setForm({ ...form, watermark_size_percent: v })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {t('admin.campaigns.edit.watermark.opacityLabel', { value: form.watermark_opacity_percent })}
                    </Label>
                    <Slider
                      min={50}
                      max={100}
                      step={1}
                      value={[form.watermark_opacity_percent]}
                      onValueChange={([v]) => setForm({ ...form, watermark_opacity_percent: v })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {t('admin.campaigns.edit.watermark.marginXLabel', { value: form.watermark_margin_x_percent })}
                    </Label>
                    <Slider
                      min={0}
                      max={100}
                      step={1}
                      value={[form.watermark_margin_x_percent]}
                      onValueChange={([v]) => setForm({ ...form, watermark_margin_x_percent: v })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {t('admin.campaigns.edit.watermark.marginYLabel', { value: form.watermark_margin_y_percent })}
                    </Label>
                    <Slider
                      min={0}
                      max={100}
                      step={1}
                      value={[form.watermark_margin_y_percent]}
                      onValueChange={([v]) => setForm({ ...form, watermark_margin_y_percent: v })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.edit.watermark.marginHelp')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.watermark.previewLabel')}</Label>
                  <WatermarkPreviewPane
                    config={previewWatermark}
                    postLayout={previewPostLayout}
                    subtitlePosition={form.enforced_subtitle_position}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <EnforcedLayoutCard
            value={form.enforced_post_layout}
            onChange={(v) => setForm({ ...form, enforced_post_layout: v })}
            watermark={previewWatermark}
            subtitlePosition={form.enforced_subtitle_position}
          />

          <EnforcedSubtitleCard
            value={form.enforced_subtitle_position}
            onChange={(v) => setForm({ ...form, enforced_subtitle_position: v })}
            postLayout={previewPostLayout}
            watermark={previewWatermark}
          />

          <EditingAssetsCard
            value={form.editing_assets}
            onChange={(v) => setForm({ ...form, editing_assets: v })}
          />

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.campaigns.edit.targetsCard')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.channels')}</Label>
                <AsyncMultiSelect
                  value={channels}
                  onChange={setChannels}
                  search={searchChannels}
                  placeholder={t('admin.campaigns.edit.fields.channelsPlaceholder')}
                  searchPlaceholder={t('admin.campaigns.edit.fields.channelsSearchPlaceholder')}
                  emptyText={t('admin.campaigns.edit.fields.emptyResults')}
                  loadingText={t('admin.campaigns.edit.fields.loading')}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>{t('admin.campaigns.edit.fields.videos')}</Label>
                  <Popover open={channelPickerOpen} onOpenChange={setChannelPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm">
                        {t('admin.campaigns.edit.fields.addAllFromChannel')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[min(28rem,calc(100vw-2rem))] p-0 z-[100]"
                      align="end"
                    >
                      <p className="px-3 pt-3 pb-2 text-xs text-muted-foreground">
                        {t('admin.campaigns.edit.fields.addAllFromChannelHint')}
                      </p>
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder={t('admin.campaigns.edit.fields.channelsSearchPlaceholder')}
                          value={channelPickerQuery}
                          onValueChange={setChannelPickerQuery}
                        />
                        <CommandList>
                          {channelPickerLoading && (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              {t('admin.campaigns.edit.fields.loading')}
                            </div>
                          )}
                          {!channelPickerLoading && channelPickerResults.length === 0 && (
                            <CommandEmpty>
                              {t('admin.campaigns.edit.fields.emptyResults')}
                            </CommandEmpty>
                          )}
                          {!channelPickerLoading && channelPickerResults.length > 0 && (
                            <CommandGroup>
                              {channelPickerResults.map((opt) => (
                                <CommandItem
                                  key={opt.id}
                                  value={opt.id}
                                  onSelect={() => addAllFromChannel(opt.id)}
                                  className="gap-2 cursor-pointer"
                                >
                                  {opt.thumbnail ? (
                                    <img
                                      src={opt.thumbnail}
                                      alt=""
                                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-muted flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm truncate">{opt.label}</div>
                                    {opt.sublabel && (
                                      <div className="text-xs text-muted-foreground truncate">
                                        {opt.sublabel}
                                      </div>
                                    )}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <AsyncMultiSelect
                  value={downloads}
                  onChange={setDownloads}
                  search={searchDownloads}
                  placeholder={t('admin.campaigns.edit.fields.videosPlaceholder')}
                  searchPlaceholder={t('admin.campaigns.edit.fields.videosSearchPlaceholder')}
                  emptyText={t('admin.campaigns.edit.fields.emptyResults')}
                  loadingText={t('admin.campaigns.edit.fields.loading')}
                />
              </div>
              {!isNew && id && (
                <div className="space-y-2">
                  <Label>{t('admin.campaigns.edit.excludedVideos.title')}</Label>
                  <ExcludedCampaignVideos
                    key={id}
                    campaignId={id}
                    videos={data?.adminCpmCampaign?.excluded_youtube_downloads || []}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.fields.admins')}</Label>
                <AsyncMultiSelect
                  value={admins}
                  onChange={setAdmins}
                  search={searchAdmins}
                  placeholder={t('admin.campaigns.edit.fields.adminsPlaceholder')}
                  searchPlaceholder={t('admin.campaigns.edit.fields.adminsSearchPlaceholder')}
                  emptyText={t('admin.campaigns.edit.fields.emptyResults')}
                  loadingText={t('admin.campaigns.edit.fields.loading')}
                />
              </div>
            </CardContent>
          </Card>

          {!isNew && id && <CampaignLiveImportCard campaignId={id} />}

          <Card>
            <CardHeader>
              <CardTitle>{t(form.campaign_type === 'ranking' ? 'admin.campaigns.edit.preApproval.allowedPlatforms' : 'admin.campaigns.edit.preApproval.cardTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.campaign_type === 'cpm' && <><p className="text-sm text-muted-foreground">
                {t('admin.campaigns.edit.preApproval.cardHint')}
              </p>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="pre-approval-enabled"
                  checked={form.pre_approval_enabled}
                  onCheckedChange={(v) =>
                    setForm({ ...form, pre_approval_enabled: v === true })}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="pre-approval-enabled"
                    className="font-medium cursor-pointer"
                  >
                    {t('admin.campaigns.edit.preApproval.enabled')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.campaigns.edit.preApproval.enabledHelp')}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pre-approval-min-score">
                  {t('admin.campaigns.edit.preApproval.minScore')}
                </Label>
                <Input
                  id="pre-approval-min-score"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={form.pre_approval_min_score}
                  onChange={(event) => setForm({
                    ...form,
                    pre_approval_min_score: Math.min(
                      100,
                      Math.max(0, Math.floor(Number(event.target.value) || 0)),
                    ),
                  })}
                />
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.edit.preApproval.minScoreHint')}
                </p>
              </div></>}
              <div className="space-y-2">
                <Label>{t('admin.campaigns.edit.preApproval.allowedPlatforms')}</Label>
                <div className="flex flex-wrap gap-4">
                  {(form.campaign_type === 'ranking' ? ['youtube', 'instagram', 'tiktok', 'facebook'] : ['youtube', 'instagram', 'tiktok']).map((platform) => (
                    <div key={platform} className="flex items-center gap-2">
                      <Checkbox
                        id={`allowed-platform-${platform}`}
                        checked={form.allowed_platforms.includes(platform)}
                        onCheckedChange={(v) => setForm({
                          ...form,
                          allowed_platforms: v === true
                            ? [...form.allowed_platforms, platform]
                            : form.allowed_platforms.filter((p) => p !== platform),
                        })}
                      />
                      <label
                        htmlFor={`allowed-platform-${platform}`}
                        className="text-sm cursor-pointer capitalize"
                      >
                        {platform}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin.campaigns.edit.preApproval.allowedPlatformsHint')}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3">
            <Button onClick={onSave}>
              {isNew
                ? t('admin.campaigns.edit.actions.create')
                : t('admin.campaigns.edit.actions.save')}
            </Button>
            {!isNew && form.campaign_type === 'cpm' && (
              <Button
                variant="outline"
                disabled={recomputing}
                onClick={() => setRecomputeOpen(true)}
              >
                {recomputing
                  ? t('admin.campaigns.edit.recompute.running')
                  : t('admin.campaigns.edit.recompute.button')}
              </Button>
            )}
            {!isNew && (
              <Button variant="destructive" onClick={onDelete}>
                {t('admin.campaigns.edit.actions.delete')}
              </Button>
            )}
          </div>

          </fieldset>
          {!isNew && form.campaign_type === 'ranking' && data?.adminCpmCampaign?.campaign_type === 'ranking' && <RankingCampaignPanel key={id} campaignId={id!} campaignStatus={data.adminCpmCampaign.status} currency={data.adminCpmCampaign.currency} reviewOnly onFinalizedChange={setRankingFinalized} />}

          <AlertDialog open={recomputeOpen} onOpenChange={setRecomputeOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('admin.campaigns.edit.recompute.dialogTitle')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('admin.campaigns.edit.recompute.dialogDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={recomputing}>
                  {t('common.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={recomputing}
                  onClick={(e) => {
                    e.preventDefault();
                    onRecompute();
                  }}
                >
                  {t('admin.campaigns.edit.recompute.confirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>
    </Layout>
  );
}
